import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { DiaryType } from "Components/DiaryCalendar";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useAppState } from "Context/StateContext";
import { Company } from "Models/company";
import {
  DespatchCrew,
  DespatchCrewCreate,
  DespatchCrewHide,
  DespatchCrewPersist,
  DespatchCrewRemove,
} from "Models/despatch.crew";
import { DespatchTask, DespatchTaskPersist } from "Models/despatch.task";
import { Job } from "Models/job";
import { Member } from "Models/members/member";
import { Filters, Searches } from "Models/shipeezi.table";
import { PlaceholderTask, Task } from "Models/task";
import { TaskComment } from "Models/task.comment";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { CompanyConnectionsService } from "Services/company.connections.service";
import DespatchService from "Services/despatch.service";
import DispatchStorageBoxService from "Services/dispatch.storage.box.service";
import FleetService from "Services/fleet.service";
import MembersService from "Services/members.service";
import OrderService from "Services/order.service";
import StorageBoxService from "Services/storage.box.service";
import TaskService from "Services/task.service";
import TasksTypesService from "Services/task.type.service";
import TeamsService from "Services/teams.service";
import UserCompanyService from "Services/user.company.service";
import { createWhereConditionForBranchId } from "Util/createWhereConditionForBranchId";
import { AvailabilityStatus, Status, ViewPerspective } from "Util/enums";
import { getIntervalByDiaryType } from "Util/getIntervalByDiaryType";
import getRelatedFilters from "Util/getRelatedFilters";
import {
  FleetKeys,
  MemberKeys,
  OrderKeys,
  StorageBoxKeys,
  TaskKeys,
  TaskTypeKeys,
  TeamKeys,
  UserCompanyKeys,
} from "Util/queries";

type CancelTask = {
  taskId: number;
  task: DespatchTask | Task;
  crew?: DespatchCrew | null;
};

type PersistTaskChanges = {
  data: DespatchTaskPersist;
};

type ManagePublishingTask = {
  taskId: number;
  task: DespatchTask;
  crew?: DespatchCrew;
};

type CreateCrew = {
  data: DespatchCrewCreate;
};

type PersistCrewChanges = {
  data: DespatchCrewPersist;
};

type RemoveCrewParams = {
  data: DespatchCrewRemove;
  createdTo: Date;
};

type HideCrewParams = {
  data: DespatchCrewHide;
  createdTo: Date;
};

export type CreateTaskPayload = {
  data: Task;
  teamId: number | null;
};

export type CreatePlaceholderTaskPayload = {
  data: PlaceholderTask;
};

export type UpdateTaskPayload = {
  taskId: number;
  task: Task & { whatHasChanged?: string[] };
};

export interface GetTasksParams {
  dateSelected?: Date;
  viewMode: DiaryType;
  page?: number;
  pageSize?: number;
  searches?: Searches;
  filters?: Filters;
  companyId: number | number[];
  branchId?: number | number[];
}

export interface GetCrewsParams {
  dateSelected?: Date;
  page?: number;
  pageSize?: number;
  searches?: Searches;
  filters?: Filters;
  companyId: number | number[];
  branchId?: number | number[];
}

interface CreateTaskComment {
  task: DespatchTask | Task;
  data: {
    taskId: number;
    userId: number;
    message: string;
  };
}
interface DeleteTaskComment {
  task: DespatchTask | Task;
  commentId: number;
}

interface GetInfiniteEquipmentsParams {
  companyId: number | number[];
  equipmentTotal: number;
  dateSelected: Date;
  branchId?: number | number[];
  pageSize: number;
  searches?: Searches;
  filter?: Filters;
  perspective?: ViewPerspective;
}

interface GetEquipmentsCountParams {
  companyId: number | number[];
  dateSelected: Date;
  branchId?: number | number[];
  searches?: Searches;
  filters?: Filters;
  perspective?: ViewPerspective;
}

interface GetInfiniteMembersParams {
  companyId: number | number[];
  memberTotal: number;
  dateSelected: Date;
  branchId?: number | number[];
  pageSize?: number;
  searches?: Searches;
  externalFilters?: Filters;
  perspective?: ViewPerspective;
  displayInDispatch?: boolean;
}

interface GetMembersCountParams {
  companyId: number | number[];
  dateSelected: Date;
  branchId?: number | number[];
  externalFilters?: Filters;
  searches?: Searches;
  perspective?: ViewPerspective;
}

interface GetInfiniteStorageBoxCountParams {
  companyId: number | number[];
  dateSelected: Date;
  branchId?: number | number[];
  searches?: Searches;
  filters?: Filters;
  perspective?: ViewPerspective;
}

interface GetInfiniteStorageBoxParams {
  companyId: number | number[];
  storageboxTotal: number;
  dateSelected: Date;
  branchId?: number | number[];
  pageSize?: number;
  searches?: Searches;
  filter?: Filters;
  perspective?: ViewPerspective;
}

export const useDespatchActions = () => {
  const { currentUser, userTimezone } = useAppState();
  const { t: translate } = useTranslation();

  const getAllMembersCount = (params: GetMembersCountParams) => {
    const { companyId, dateSelected, branchId, externalFilters, searches, perspective } = params;

    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    const filters = {
      companyId,
      status: Status.ACTIVE,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      include: JSON.stringify([
        {
          association: "branches",
          required: true,
          where: createWhereConditionForBranchId(branchId),
        },
        {
          association: "userCompany",
          attributes: ["leader"],
        },
        {
          association: "availabilities",
          required: false,
          where: {
            status: [AvailabilityStatus.IN_PROGRESS, AvailabilityStatus.PENDING],
          },
        },
        {
          association: "certifications",
        },
      ]),
      startDate: startDateISO,
      endDate: endDateISO,
      perspective,
      distinctCount: true,
      displayInDispatch: true,
      ...externalFilters,
    };

    return useQuery({
      queryKey: MemberKeys.count(["despatch.members.count", ...Object.values(filters)]),
      queryFn: async () => {
        const response = await MembersService.count(filters);

        if (response?.data) return response.data;

        return 0;
      },
      enabled: !!companyId,
      refetchOnWindowFocus: false,
    });
  };

  const getAllEquipmentsCount = (params: GetEquipmentsCountParams) => {
    const { companyId, dateSelected, branchId, searches, filters, perspective } = params;
    const includes = [
      {
        association: "availabilities",
        required: false,
        where: {
          status: [AvailabilityStatus.IN_PROGRESS, AvailabilityStatus.PENDING],
        },
      },
      {
        association: "branches",
        required: false,
        where: createWhereConditionForBranchId(branchId),
      },
    ];

    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    return useQuery({
      queryKey: FleetKeys.count([
        "despatch.equipment.count",
        companyId,
        searches,
        filters,
        branchId,
        includes,
      ]),
      queryFn: async () => {
        const response = await FleetService.count({
          companyId,
          status: Status.ACTIVE,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: includes ? JSON.stringify(includes) : "",
          startDate: startDateISO,
          endDate: endDateISO,
          perspective,
          distinctCount: true,
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
    });
  };

  const getStorageBoxesCount = (params: GetInfiniteStorageBoxCountParams) => {
    const { companyId, dateSelected, branchId, searches, filters, perspective } = params;
    const isFleet = 0;
    const singleUse = 0;
    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    return useQuery({
      queryKey: StorageBoxKeys.count([companyId, searches, filters, isFleet, singleUse]),
      queryFn: async () => {
        const response = await DispatchStorageBoxService.count({
          companyId,
          isFleet,
          singleUse,
          include: JSON.stringify([
            {
              association: "storageStatus",
              where: {
                ...getRelatedFilters(filters, "storageStatus"),
              },
            },
            {
              association: "storageBoxType",
              where: {
                ...getRelatedFilters(filters, "storageBoxType"),
              },
            },
            {
              association: "branches",
              required: false,
              where: createWhereConditionForBranchId(branchId),
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          startDate: startDateISO,
          endDate: endDateISO,
          perspective,
          distinctCount: true,
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return 0;
      },
    });
  };

  const getTeamsCount = (
    companyId: number[],
    dateSelected: Date,
    searches?: Searches,
    filters?: Filters,
    perspective?: ViewPerspective
  ) => {
    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    return useQuery({
      queryKey: TeamKeys.count([companyId, searches, filters]),
      queryFn: async () => {
        const response = await TeamsService.count({
          isActive: 1,
          isCrew: false,
          companyId,
          include: JSON.stringify([
            {
              association: "memberLeader",
            },
            {
              association: "members",
            },
            {
              association: "fleets",
            },
            {
              association: "branches",
              where: {
                id: currentUser?.branches?.map((branch) => branch.id),
              },
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          startDate: startDateISO,
          endDate: endDateISO,
          perspective,
          distinctCount: true,
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return 0;
      },
    });
  };

  const getInfiniteMembers = (params: GetInfiniteMembersParams) => {
    const {
      companyId,
      memberTotal,
      dateSelected,
      branchId,
      pageSize = 10,
      searches,
      externalFilters,
      perspective,
    } = params;
    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );
    const filters = {
      companyId,
      status: Status.ACTIVE,
      searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
      sortBy: JSON.stringify([["fullName", "asc"]]),
      startDate: startDateISO,
      endDate: endDateISO,
      include: JSON.stringify([
        {
          association: "branches",
          required: false,
          where: createWhereConditionForBranchId(branchId),
        },
        {
          association: "userCompany",
          attributes: ["leader"],
        },
        {
          association: "availabilities",
          required: false,
          where: {
            status: [AvailabilityStatus.IN_PROGRESS, AvailabilityStatus.PENDING],
          },
        },
        {
          association: "certifications",
        },
      ]),
      perspective,
      ...externalFilters,
      displayInDispatch: true,
    };

    return useInfiniteQuery({
      queryKey: MemberKeys.getAll([
        "despatch-more",
        pageSize,
        searches,
        branchId,
        ...Object.values(filters),
      ]),
      queryFn: async ({ pageParam = 0 }) => {
        const response = await MembersService.getAll({
          ...filters,
          page: pageParam,
          pageSize,
        } as any);

        if (response?.data) return response.data;

        return [];
      },
      getNextPageParam: (_, allPages) => {
        return allPages?.length < Math.ceil(memberTotal / pageSize) ? allPages.length : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!companyId,
    });
  };

  const getInfiniteEquipments = (params: GetInfiniteEquipmentsParams) => {
    const {
      companyId,
      equipmentTotal,
      dateSelected,
      branchId,
      pageSize = 10,
      searches,
      filter,
      perspective,
    } = params;

    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );
    const filters = {
      searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
      companyId,
      status: Status.ACTIVE,
      include: JSON.stringify([
        {
          association: "branches",
          required: false,
          where: createWhereConditionForBranchId(branchId),
        },
        {
          association: "availabilities",
          required: false,
          where: {
            status: [AvailabilityStatus.IN_PROGRESS, AvailabilityStatus.PENDING],
          },
        },
      ]),
      sortBy: JSON.stringify([
        ["equipmentType", "asc"],
        ["description", "asc"],
        ["fleetNumber", "asc"],
      ]),
      startDate: startDateISO,
      endDate: endDateISO,
      perspective,
      ...filter,
    };

    return useInfiniteQuery({
      queryKey: FleetKeys.getAll([
        "despatch-more",
        pageSize,
        searches,
        branchId,
        ...Object.values(filters),
      ]),
      queryFn: async ({ pageParam = 0 }) => {
        const response = await FleetService.getAll({
          ...filters,
          page: pageParam,
          pageSize,
        });

        if (response?.data) return response.data;

        return [];
      },
      getNextPageParam: (_, allPages) => {
        return allPages?.length < Math.ceil(equipmentTotal / pageSize)
          ? allPages.length
          : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!companyId,
    });
  };

  const getInfiniteStorageBoxes = (params: GetInfiniteStorageBoxParams) => {
    const {
      companyId,
      storageboxTotal,
      dateSelected,
      branchId,
      pageSize = 10,
      searches,
      filter,
      perspective,
    } = params;
    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    const filters: any = {
      companyId,
      isFleet: 0,
      singleUse: 0,
      isActive: 1,
      include: JSON.stringify([
        {
          association: "storageStatus",
        },
        {
          association: "storageBoxType",
        },
        {
          association: "branches",
          required: false,
          where: createWhereConditionForBranchId(branchId),
        },
      ]),
      searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
      sortBy: JSON.stringify([["number", "asc"]]),
      ...filter,
      startDate: startDateISO,
      endDate: endDateISO,
      perspective,
    };

    return useInfiniteQuery({
      queryKey: StorageBoxKeys.getAll([
        "despatch-more",
        pageSize,
        searches,
        ...Object.values(filters),
      ]),
      queryFn: async ({ pageParam = 0 }) => {
        const response = await DispatchStorageBoxService.list({
          ...filters,
          page: pageParam,
          pageSize,
        });

        if (response?.data) return response.data;

        return [];
      },
      getNextPageParam: (_, allPages) => {
        return allPages?.length < Math.ceil(storageboxTotal / pageSize)
          ? allPages.length
          : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!companyId,
    });
  };

  const getInfiniteTeams = (
    companyId: number[],
    teamTotal: number,
    dateSelected: Date,
    pageSize = 10,
    searches?: Searches,
    filter?: Filters,
    perspective?: ViewPerspective
  ) => {
    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );
    const filters = {
      isActive: 1,
      isCrew: false,
      companyId,
      searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
      sortBy: JSON.stringify([["name", "asc"]]),
      include: JSON.stringify([
        {
          association: "memberLeader",
        },
        {
          association: "members",
        },
        {
          association: "fleets",
        },
        {
          association: "branches",
          where: {
            id: currentUser?.branches?.map((branch) => branch.id),
          },
        },
      ]),
      startDate: startDateISO,
      endDate: endDateISO,
      perspective,
      ...filter,
    };

    return useInfiniteQuery({
      queryKey: TeamKeys.getAll(["despatch", pageSize, searches, ...Object.values(filters)]),
      queryFn: async () => {
        const response = await TeamsService.getAll(filters as any);

        if (response?.data)
          return response.data.map((team) => ({
            ...team,
            members: team.memberLeaderId
              ? team.members?.filter((member) => member.id !== team.memberLeaderId)
              : team.members,
          }));

        return [];
      },
      getNextPageParam: (_, allPages) => {
        return allPages?.length < Math.ceil(teamTotal / pageSize) ? allPages.length : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!companyId,
    });
  };

  const getCrews = (params: GetCrewsParams) => {
    const {
      dateSelected = new Date(),
      page = 0,
      pageSize = 10,
      searches,
      companyId,
      branchId,
      filters: tableFilters,
    } = params;

    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      DiaryType.RESOURCES,
      userTimezone.name
    );

    const filters = {
      companyId,
      branchId,
      startDate: startDateISO,
      endDate: endDateISO,
      filters: tableFilters,
    };

    const payload = {
      ...filters,
      searchByOr: searches?.length ? JSON.stringify(searches) : undefined,
    };

    return useQuery({
      queryKey: TeamKeys.getAll([
        "despatch-crews",
        page,
        pageSize,
        searches,
        ...Object.values(payload),
      ]),
      queryFn: async () => {
        return DespatchService.getTasksByTeam(payload).then(({ data }) => data);
      },
      enabled: !!companyId,
      refetchOnWindowFocus: false,
    });
  };

  const getTasks = (params: GetTasksParams) => {
    const {
      dateSelected = new Date(),
      viewMode,
      page = 0,
      pageSize = 10,
      searches,
      companyId,
      branchId,
      filters: tableFilters,
    } = params;

    const { startDateISO, endDateISO } = getIntervalByDiaryType(
      dateSelected,
      viewMode,
      userTimezone.name
    );

    const filters = {
      companyId,
      branchId,
      startDate: startDateISO,
      endDate: endDateISO,
    };

    const payload = {
      ...tableFilters,
      ...filters,
      searchByOr: searches?.length ? JSON.stringify(searches) : undefined,
    };

    return useQuery({
      queryKey: TaskKeys.getAll(["despatch-tasks", page, pageSize, ...Object.values(payload)]),
      queryFn: async () => {
        return DespatchService.getTasks(payload).then(({ data }) => data);
      },
      enabled: !!companyId,
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    });
  };

  const getOrders = (
    companyId: number | number[],
    params: { searches?: Searches; page?: number; pageSize?: number } = {}
  ) => {
    const { searches, page, pageSize } = params;
    const filters = searches?.flatMap((search) => Object.values(search)) ?? [];
    const branchId = currentUser?.branches?.map((branch) => branch.id!) ?? [];

    return useQuery({
      queryKey: OrderKeys.getAll([companyId, ...branchId, ...filters]),
      queryFn: async () => {
        const response = await OrderService.getAll({
          companyId,
          userId: currentUser?.id,
          branchId,
          include: JSON.stringify([
            {
              association: "origins",
            },
            {
              association: "destinations",
            },
          ]),
          searchByOr: searches?.length ? JSON.stringify(searches) : "",
          sortBy: JSON.stringify([["reference", "asc"]]),
          page,
          pageSize,
        });

        if (response?.data) return response?.data as Job[];

        return [];
      },
      initialData() {
        return [];
      },
      enabled: !!companyId && !!searches?.length && !!currentUser,
      refetchOnWindowFocus: false,
    });
  };

  const useCancelTask = (
    onSuccess: (task: DespatchTask | Task, crew?: DespatchCrew | null) => void
  ) => {
    return useMutation({
      mutationKey: TaskKeys.cancelFromDespatch(),
      mutationFn: async (variables: CancelTask) => {
        return DespatchService.cancelTask(variables.taskId);
      },
      onSuccess: (_, variables) => {
        onSuccess(variables.task, variables.crew);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const usePublishTask = (onSuccess: (task: DespatchTask) => void) => {
    return useMutation({
      mutationKey: TaskKeys.publishFromDespatch(),
      mutationFn: async (variables: ManagePublishingTask) => {
        return DespatchService.publishTask([variables.taskId]);
      },
      onSuccess: (_, variables) => {
        onSuccess(variables.task);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useUnpublishTask = (onSuccess: (task: DespatchTask) => void) => {
    return useMutation({
      mutationKey: TaskKeys.unpublishFromDespatch(),
      mutationFn: async (variables: ManagePublishingTask) => {
        return DespatchService.unpublishTask([variables.taskId]);
      },
      onSuccess: (_, variables) => {
        onSuccess(variables.task);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useCreateCrew = (onSuccess: (data: Team, variables: CreateCrew) => void) => {
    return useMutation({
      // @ts-ignore
      mutationKey: TeamKeys.createCrew(),
      mutationFn: async (variables: CreateCrew) => {
        return DespatchService.createCrew(variables.data);
      },
      onSuccess: (response: AxiosResponse<Team>, variables) => {
        onSuccess(response.data, variables);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const usePersistTaskChanges = (onSuccess?: (variables: PersistTaskChanges) => void) => {
    return useMutation({
      // @ts-ignore
      mutationKey: TeamKeys.persistCrewChanges(),
      mutationFn: async (variables: PersistTaskChanges) => {
        return DespatchService.persistTaskData(variables.data);
      },
      onSuccess: (response: AxiosResponse<Team>, variables) => {
        onSuccess?.(variables);
        // NotificationToast.success(translate("thisChangeWillBeProcessedShortly"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const usePersistCrewChanges = (onSuccess?: (variables: PersistCrewChanges) => void) => {
    return useMutation({
      // @ts-ignore
      mutationKey: TeamKeys.persistCrewChanges(),
      mutationFn: async (variables: PersistCrewChanges) => {
        return DespatchService.persistCrewData(variables.data);
      },
      onSuccess: (response: AxiosResponse<Team>, variables) => {
        onSuccess?.(variables);
        // NotificationToast.success(translate("thisChangeWillBeProcessedShortly"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const usePersistCrewsChanges = (onSuccess?: (variables: DespatchCrewPersist[]) => void) => {
    return useMutation({
      // @ts-ignore
      mutationKey: ["usePersistCrewsChanges"],
      mutationFn: async (variables: DespatchCrewPersist[]) => {
        return DespatchService.persistCrewsData(variables);
      },
      onSuccess: (response: AxiosResponse<Team>, variables) => {
        onSuccess?.(variables);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useRemoveCrew = (onSuccess?: (crew: DespatchCrew) => void) => {
    return useMutation({
      mutationKey: TaskKeys.cancelFromDespatch(),
      mutationFn: async (variables: RemoveCrewParams) => {
        return DespatchService.removeCrew(variables.data, variables.createdTo);
      },
      onSuccess: (_, variables) => {
        onSuccess?.(variables.data);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useHideCrew = (onSuccess?: (crew: DespatchCrew) => void) => {
    return useMutation({
      mutationKey: TaskKeys.cancelFromDespatch(),
      mutationFn: async (variables: HideCrewParams) => {
        return DespatchService.hideCrew(variables.data, variables.createdTo);
      },
      onSuccess: (_, variables) => {
        onSuccess?.(variables.data);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const getCompaniesWithConnection = () => {
    return useQuery({
      queryKey: ["companies-with-connection", currentUser?.companyId],
      queryFn: async () => {
        return CompanyConnectionsService.getCompaniesWithConnection().then(({ data }) => data);
      },
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.id,
      initialData() {
        return [];
      },
    });
  };

  const getCompaniesByUser = () => {
    const filters = {
      userId: currentUser?.id,
      status: Status.ACTIVE,
      include: JSON.stringify([
        {
          association: "company",
          attributes: ["id", "name"],
          include: [
            { association: "icon" },
            {
              association: "profile",
              attributes: ["id", "name"],
              required: true,
              where: {
                name: "Moving",
              },
            },
          ],
          where: { status: Status.ACTIVE },
        },
      ]),
      sortBy: JSON.stringify([["company", "name", "asc"]]),
    };

    return useQuery({
      queryKey: UserCompanyKeys.getAll([...Object.values(filters)]),
      queryFn: async () => {
        const response = await UserCompanyService.getAll(filters);

        if (response.data) {
          const companies = response.data.map((userCompany) => userCompany.company);

          return companies || [];
        }

        return [] as Company[];
      },
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.id,
      initialData() {
        return [];
      },
    });
  };

  const getAllTaskTypes = () => {
    const taskTypeFilters = {
      companyId: currentUser?.companyId,
      status: Status.ACTIVE,
      sortBy: JSON.stringify([["name", "ASC"]]),
    };

    return useQuery({
      queryKey: TaskTypeKeys.getAll(["despatch", ...Object.values(taskTypeFilters)]),
      queryFn: async () => {
        const response = await TasksTypesService.getAll(taskTypeFilters);

        return response && response.data && Array.isArray(response.data) ? response.data : [];
      },
      initialData() {
        return [];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      // cacheTime: CACHE_TIME,
      // staleTime: STALE_TIME,
    });
  };

  const useCreateTask = (onSuccess: (response: Task, variables: CreateTaskPayload) => void) => {
    return useMutation({
      mutationKey: TaskKeys.create(),
      mutationFn: async (variables: CreateTaskPayload) => {
        return TaskService.create(variables.data);
      },
      onSuccess: (response, variables) => {
        onSuccess(response.data, variables);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useCreatePlaceholderTask = (
    onSuccess: (response: Task, variables: CreatePlaceholderTaskPayload) => void
  ) => {
    return useMutation({
      mutationKey: TaskKeys.create(),
      mutationFn: async (variables: CreatePlaceholderTaskPayload) => {
        return DespatchService.createPlaceholderTask(variables.data);
      },
      onSuccess: (response, variables) => {
        onSuccess(response.data, variables);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useDeletePlaceholderTask = (onSuccess: (taskId: number) => void) => {
    return useMutation({
      mutationKey: TaskKeys.delete(),
      mutationFn: async (id: number) => {
        return DespatchService.deletePlaceholderTask(id);
      },
      onSuccess: (_, taskId) => {
        onSuccess(taskId);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useUpdateTask = (onSuccess: (variables: Task) => void) => {
    return useMutation({
      mutationKey: TaskKeys.update(),
      mutationFn: async (variables: UpdateTaskPayload) => {
        return TaskService.update(variables.taskId, variables.task);
      },
      onSuccess: (_, variables) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        onSuccess(variables.task);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useCreateTaskComment = (
    onSuccess: (response: TaskComment, variables: CreateTaskComment) => void
  ) => {
    return useMutation({
      mutationKey: TaskKeys.create(),
      mutationFn: async (variables: CreateTaskComment) => {
        return DespatchService.createTaskComment(variables.data);
      },
      onSuccess: (response, variables) => {
        onSuccess(response.data, variables);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useDeleteTaskComment = (
    onSuccess: (response: any, variables: DeleteTaskComment) => void
  ) => {
    return useMutation({
      mutationKey: TaskKeys.create(),
      mutationFn: async (variables: DeleteTaskComment) => {
        return DespatchService.deleteTaskComment({ id: variables.commentId });
      },
      onSuccess: (response, variables) => {
        onSuccess(response.data, variables);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const getMembersList = () => {
    return useQuery({
      queryKey: MemberKeys.getAll(["despatch-crew-create", currentUser?.companyId]),
      queryFn: async () => {
        const response = await MembersService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["fullName", "asc"]]),
          include: JSON.stringify([
            {
              association: "userCompany",
              required: false,
              where: {
                companyId: currentUser?.companyId,
              },
            },
          ]),
        });

        if (response?.data && Array.isArray(response?.data)) {
          return response.data;
        }

        return [] as Member[];
      },
      initialData() {
        return [];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    });
  };

  const getFleetList = () => {
    return useQuery({
      queryKey: FleetKeys.getAll(["despatch-crews-create", currentUser?.companyId]),
      queryFn: async () => {
        const response = await FleetService.getAll({
          companyId: currentUser?.companyId,
          attributes: JSON.stringify(["id", "fleetNumber", "description", "equipmentType", "ratePerHour"]),
        });

        if (response?.data) return response.data;

        return [];
      },
      initialData() {
        return [];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    });
  };

  const getStorageBoxes = () => {
    return useQuery(
      ["storage.box.list", currentUser?.companyId],
      () =>
        StorageBoxService.getAll({
          companyId: currentUser?.companyId,
          isActive: 1,
          sortBy: JSON.stringify([["number", "asc"]]),
          include: JSON.stringify([
            {
              association: "warehouses",
            },
            { association: "storageBoxType" },
          ]),
        }).then(({ data }) => data),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        initialData() {
          return [];
        },
      }
    );
  };

  return {
    getTasks,
    getCrews,
    getAllMembersCount,
    getAllEquipmentsCount,
    getStorageBoxesCount,
    getTeamsCount,
    getInfiniteTeams,
    getOrders,
    useCancelTask,
    usePublishTask,
    useUnpublishTask,
    useCreateCrew,
    usePersistTaskChanges,
    usePersistCrewChanges,
    usePersistCrewsChanges,
    getInfiniteMembers,
    getInfiniteEquipments,
    getInfiniteStorageBoxes,
    useRemoveCrew,
    useHideCrew,
    getCompaniesByUser,
    getAllTaskTypes,
    useCreatePlaceholderTask,
    useDeletePlaceholderTask,
    useCreateTask,
    useCreateTaskComment,
    useDeleteTaskComment,
    useUpdateTask,
    getMembersList,
    getFleetList,
    getStorageBoxes,
    getCompaniesWithConnection,
  };
};
